import { useState, useRef, useEffect } from "react";
import { CalendarPlus, ChevronDown } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { getRegistrationUrl, EVENT_ENDED } from "@/config";

const EVENT_2026 = {
  title: "OBAL ROKU SUMMIT 2026",
  location: "Hotel Aquapalace Praha, Pražská 137, Čestlice",
  start: "20261015T123000",
  end: "20261015T220000",
  allDay: false,
  uid: "obal-roku-summit-2026@obalroku.cz",
  file: "obal-roku-summit-2026.ics",
  tz: "Europe/Prague",
};

const EVENT_2027 = {
  title: "OBAL ROKU SUMMIT 2027",
  location: "Praha",
  startDate: "20271021",
  endDate: "20271022",
  allDay: true,
  uid: "obal-roku-summit-2027@obalroku.cz",
  file: "obal-roku-summit-2027.ics",
  tz: "Europe/Prague",
};

const EVENT = EVENT_ENDED ? EVENT_2027 : EVENT_2026;

const googleUrl = (desc) => {
  const dates = EVENT.allDay ? `${EVENT.startDate}/${EVENT.endDate}` : `${EVENT.start}/${EVENT.end}`;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: EVENT.title,
    dates,
    details: desc,
    location: EVENT.location,
    ctz: EVENT.tz,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const downloadIcs = (desc) => {
  const dateLines = EVENT.allDay
    ? [`DTSTART;VALUE=DATE:${EVENT.startDate}`, `DTEND;VALUE=DATE:${EVENT.endDate}`]
    : [`DTSTART;TZID=${EVENT.tz}:${EVENT.start}`, `DTEND;TZID=${EVENT.tz}:${EVENT.end}`];
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Obal roku Summit//CS",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${EVENT.uid}`,
    "DTSTAMP:20260101T000000Z",
    ...dateLines,
    `SUMMARY:${EVENT.title}`,
    `DESCRIPTION:${desc.replace(/\n/g, "\\n")}`,
    `LOCATION:${EVENT.location}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = EVENT.file;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const AddToCalendar = ({ variant = "ghost" }) => {
  const { t, lang } = useLanguage();
  const c = t.calendar;
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const desc = EVENT_ENDED
    ? `${EVENT.title}\n${t.hero.nextEditionValue}`
    : `${t.hero.tagline}\n${t.hero.dateValue} · ${EVENT.location}\nRegistrace: ${getRegistrationUrl(lang)}`;

  const triggerCls =
    variant === "solid"
      ? "inline-flex items-center gap-2 bg-white/5 border border-white/20 hover:border-[#CEA26B] text-white px-4 py-2.5 text-sm font-medium tracking-wide transition-colors"
      : "inline-flex items-center gap-2 text-sm font-medium text-[#CEA26B] hover:text-[#FFCB85] transition-colors";

  return (
    <div className="relative inline-block text-left" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="add-to-calendar-btn"
        className={triggerCls}
      >
        <CalendarPlus className="h-4 w-4" />
        {c.add}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-2 z-30 min-w-[220px] bg-[#0A2060] border border-white/10 py-2 shadow-2xl">
          <a
            href={googleUrl(desc)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            data-testid="cal-google"
            className="block px-5 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
          >
            {c.google}
          </a>
          <button
            type="button"
            onClick={() => {
              downloadIcs(desc);
              setOpen(false);
            }}
            data-testid="cal-ics"
            className="block w-full text-left px-5 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
          >
            {c.ics}
          </button>
        </div>
      )}
    </div>
  );
};
